/*
 * Stage 3 — resync the tester-unit discount right before checkout.
 *
 * When the customer starts checkout from EITHER entry point:
 *   - full cart page: #checkout button / form#cart
 *   - cart drawer:     #CartDrawer-Checkout button / form#CartDrawer-Form
 * this intercepts the action, POSTs the current cart's full Storefront GID
 * (`gid://shopify/Cart/<token>?key=<key>`, straight from /cart.js) to the
 * mmsco-wholesale-api endpoint (POST /api/tester-discount), waits for it, then
 * navigates to /checkout.
 *
 * The endpoint is authoritative: on every call it runs `cartDiscountCodesUpdate`
 * on that cart — setting the freshly minted code, or wiping any stale one — so
 * by the time this resolves the cart already carries exactly the right discount
 * (or none). Nothing needs to be threaded through a /discount/<code> URL: every
 * branch here just goes to /checkout.
 *
 * ON CART-PAGE LOAD, AFTER EVERY IN-PLACE CART MUTATION, AND ON bfcache RESTORE
 * this also fires the same resync in the background, then re-renders the footer,
 * so a discount that no longer applies clears itself (total included) without
 * the customer having to press checkout again:
 *   - load: a one-off call at script init.
 *   - mutation: subscribes to PUB_SUB_EVENTS.cartUpdate, which cart.js & friends
 *     publish after every AJAX cart change (qty stepper, line removal, tester
 *     line add/remove). Every source is acted on — unlike tester-line.js this
 *     does NOT skip its own 'tester-line' source, since adding/removing a tester
 *     line is exactly when the discount must be recomputed.
 *   - bfcache: a `pageshow` with `event.persisted` (back-nav that restored the
 *     page from cache without re-running this script).
 * This file is only ever loaded by sections/main-cart-footer.liquid, which only
 * renders on the /cart page, so "this ran" already means "we're on the cart
 * page" — no page detection needed (revisit if cart drawer support is ever
 * added). The background call is fire-and-forget: it never blocks the checkout
 * listeners or the cart UI update the triggering event is already driving,
 * never shows the customer an error or a spinner, and on any failure just
 * leaves the current cart display alone — the Liquid-side backstop in
 * main-cart-footer.liquid / cart-drawer.liquid hides a stale TESTER- row until
 * the resync lands.
 *
 * FAIL OPEN, ALWAYS. A non-2xx / network error, a timeout, a missing config, or
 * any thrown error -> still navigate to /checkout, exactly as if this feature
 * did not exist. This feature failing must never block checkout.
 *
 * Follows the assets/order-minimums.js pattern: IIFE + window.__xInit guard,
 * config from a data-* element (#tester-discount-config) rather than hardcoded,
 * document-level capture-phase click / submit handlers using the same
 * stopImmediatePropagation()-when-intercepting idiom. This script's tag loads
 * AFTER order-minimums.js so a minimums violation short-circuits (via its
 * stopImmediatePropagation) before any discount call is made.
 *
 * Reads the cart only (never mutates it client-side) — how tester lines are
 * added/removed lives in assets/tester-line.js and is untouched here.
 */
(function () {
  if (window.__testerDiscountCheckoutInit) return;

  const config = document.getElementById('tester-discount-config');
  const apiUrl = config && config.dataset.apiUrl ? config.dataset.apiUrl.trim() : '';
  // No configured endpoint -> do nothing at all; checkout proceeds normally.
  if (!apiUrl) return;
  window.__testerDiscountCheckoutInit = true;

  const ROOT_URL = (config.dataset.rootUrl || '/').replace(/\/+$/, '');
  const REQUEST_TIMEOUT_MS = 4000;

  const CHECKOUT_BUTTON_IDS = ['checkout', 'CartDrawer-Checkout'];
  const CHECKOUT_FORM_IDS = ['cart', 'CartDrawer-Form'];

  // One request at a time; a second click/submit while working is swallowed.
  let inFlight = false;

  const getCartToken = async () => {
    const response = await fetch(`${routes.cart_url}.js`, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`/cart.js responded ${response.status}`);
    const cart = await response.json();
    if (!cart || !cart.token) throw new Error('cart token missing from /cart.js');
    return cart.token;
  };

  const syncCartDiscount = async (cartId) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cartId }),
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`tester-discount API responded ${response.status}`);
    } finally {
      clearTimeout(timer);
    }
  };

  const setBusy = (control, busy) => {
    if (!control) return;
    control.toggleAttribute('aria-busy', busy);
    control.disabled = busy;
  };

  // Re-render the cart footer (the cart-level discount list + totals) in place,
  // using the GET Section Rendering call the theme already uses in cart.js
  // (onCartUpdate). A GET section render can't mutate the cart, so this is a
  // safe no-op on cart contents.
  const refreshCartFooter = async () => {
    const footer = document.getElementById('main-cart-footer');
    if (!footer) return;
    const sectionId = footer.dataset.id || 'main-cart-footer';
    const response = await fetch(
      `${routes.cart_url}?section_id=${encodeURIComponent(sectionId)}`,
      { credentials: 'same-origin' }
    );
    if (!response.ok) return;
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const fresh = doc.querySelector('#main-cart-footer .js-contents');
    const live = footer.querySelector('.js-contents');
    if (fresh && live) live.innerHTML = fresh.innerHTML;
  };

  // One resync pass: sync the cart's discount, then re-render the footer.
  // Fire-and-forget — on any failure, log and stop; the current cart display is
  // left as-is (the Liquid backstop hides a stale TESTER- row in the meantime).
  // syncCartDiscount already caps itself at REQUEST_TIMEOUT_MS.
  const resyncAndRefresh = async () => {
    try {
      const cartId = `gid://shopify/Cart/${await getCartToken()}`;
      await syncCartDiscount(cartId);
    } catch (error) {
      console.error('[tester-discount-checkout] background cart resync skipped', error);
      return;
    }
    try {
      await refreshCartFooter();
    } catch (error) {
      console.error('[tester-discount-checkout] cart footer refresh after resync failed', error);
    }
  };

  // Non-blocking guard. This must never delay the cart UI update the triggering
  // event is already driving, so it does NOT use the checkout intercept's
  // blocking inFlight flag. If a pass is already running, remember one more is
  // due and run exactly one when it finishes (so the final cart state always
  // gets synced), collapsing any burst of events in between.
  let resyncRunning = false;
  let resyncPending = false;
  const scheduleResync = () => {
    if (resyncRunning) {
      resyncPending = true;
      return;
    }
    resyncRunning = true;
    Promise.resolve()
      .then(resyncAndRefresh)
      .finally(() => {
        resyncRunning = false;
        if (resyncPending) {
          resyncPending = false;
          scheduleResync();
        }
      });
  };

  const goToCheckout = () => {
    window.location.assign(`${ROOT_URL}/checkout`);
  };

  const intercept = async (control) => {
    if (inFlight) return;
    inFlight = true;
    setBusy(control, true);
    try {
      const cartId = `gid://shopify/Cart/${await getCartToken()}`;
      await syncCartDiscount(cartId);
    } catch (error) {
      // Fail open — go to checkout regardless of what went wrong here.
      console.error('[tester-discount-checkout] discount resync skipped', error);
    }
    goToCheckout();
    // We are navigating away; leave inFlight/busy set so a stray second event
    // during the navigation is ignored.
  };

  const matchingButton = (event) => {
    if (!event.target || !event.target.closest) return null;
    return event.target.closest(CHECKOUT_BUTTON_IDS.map((id) => `#${id}`).join(', '));
  };

  document.addEventListener(
    'click',
    (event) => {
      const control = matchingButton(event);
      if (!control) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      intercept(control);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form || CHECKOUT_FORM_IDS.indexOf(form.id) === -1) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const buttonId = form.id === 'CartDrawer-Form' ? 'CartDrawer-Checkout' : 'checkout';
      intercept(document.getElementById(buttonId));
    },
    true
  );

  // Re-run after every AJAX cart mutation. cart.js publishes cartUpdate with
  // source 'cart-items' for the in-place qty/remove controls (the repro path),
  // tester-line.js with 'tester-line' for tester add/remove, others for
  // quick-add etc. All of them can change what discount is owed, so none are
  // filtered out. Our own refreshCartFooter does a GET section render and
  // publishes nothing, so this can't feed back on itself. Guarded so a missing
  // pub/sub (constants.js / pubsub.js failed to load) can't crash the IIFE and
  // take the checkout intercept down with it.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => scheduleResync());
  } else {
    console.error('[tester-discount-checkout] pub/sub unavailable; cart-mutation resync disabled');
  }

  // bfcache: a back-navigation that restored the page from cache runs no page
  // scripts, so the init call below never fires on that navigation. pageshow
  // with event.persisted covers it. (persisted is false on a normal load, which
  // the init call already handles — no double run.)
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) scheduleResync();
  });

  // Checkout listeners are live above; kick off the initial resync in the
  // background (not awaited). A race with a checkout press mid-flight is
  // acceptable — worst case one extra one-time code is minted, still fail-open.
  scheduleResync();
})();
